import { Button, Form, Popup, Text } from '@approvalmax/ui/src/components';
import { selectors } from 'modules/common';
import { domain } from 'modules/data';
import { FC, memo, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useCompanyBasedOnUrl } from 'shared/hooks/useCompanyBasedOnUrl';

import { budgetPeriodToDate, dateToBudgetPeriod } from '../../helpers';
import { useSetBudgetOptionsMutation } from '../../hooks/useSetBudgetOptionsMutation';
import CheckPeriodFormPart from '../CheckPeriodFormPart/CheckPeriodFormPart';
import ZeroAmountFormPart from '../ZeroAmountFormPart/ZeroAmountFormPart';
import { messages } from './BudgetSettingsPopupContent.messages';
import { BudgetSettingsPopupContentProps, FormValues } from './BudgetSettingsPopupContent.types';

const BudgetSettingsPopupContent: FC<BudgetSettingsPopupContentProps> = memo((props) => {
    const { budget, toggleIsOpenPopup } = props;

    const company = useCompanyBasedOnUrl();
    const companyId = company.id;
    const budgetId = budget.id;
    const integrationName = selectors.integration.getIntegrationTypeName(company.integration!.integrationType);
    const isZeroAmountsAvailable =
        company.integration?.integrationType === domain.IntegrationType.Xero
            ? company.betaFeatures.includes(domain.CompanyBetaFeature.BudgetingZeroAmounts)
            : true;

    const { setBudgetOptions, isLoading, isSuccess } = useSetBudgetOptionsMutation(company);

    const form = useForm<FormValues>({
        values: {
            companyId,
            budgetId,
            checkPeriod: budget.checkPeriod || domain.BudgetCheckPeriod.Monthly,
            checkPeriodFirstMonth: budget.checkPeriodFirstMonth,
            zeroAmountHandlingType: budget.zeroAmountHandlingType || domain.BudgetZeroAmountHandlingType.NotConsider,
            checkPeriodFrom: budgetPeriodToDate(budget.checkPeriodFrom),
            checkPeriodTill: budgetPeriodToDate(budget.checkPeriodTill),
        },
    });

    const onSubmit = form.handleSubmit((data) => {
        if (
            data.checkPeriod !== domain.BudgetCheckPeriod.AnnuallyWithCustomFirstMonth &&
            data.checkPeriod !== domain.BudgetCheckPeriod.YearToDateWithCustomFirstMonth
        ) {
            delete data.checkPeriodFirstMonth;
        }

        setBudgetOptions({
            ...data,
            checkPeriodFrom: dateToBudgetPeriod(data.checkPeriodFrom),
            checkPeriodTill: dateToBudgetPeriod(data.checkPeriodTill),
        });
    });

    useEffect(() => {
        if (isSuccess) {
            toggleIsOpenPopup(false);
        }
    }, [isSuccess, toggleIsOpenPopup]);

    return (
        <Form form={form} onSubmit={onSubmit}>
            <Popup.Header
                title={messages.title({
                    budgetName: budget.name,
                })}
                actions={
                    <Button disabled={isLoading} type='submit' color='blue80' size='medium'>
                        {messages.saveButton}
                    </Button>
                }
                progress={isLoading}
            />

            <Popup.Body>
                <Text font='body' spacing='0 0 24'>
                    {messages.description({
                        name: <b>{budget.name}</b>,
                        br: <br />,
                        integrationName,
                    })}
                </Text>

                <CheckPeriodFormPart origin={budget.origin} />

                {isZeroAmountsAvailable && <ZeroAmountFormPart />}
            </Popup.Body>
        </Form>
    );
});

export default BudgetSettingsPopupContent;
